// hooks/useCountdownTo.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const toMs = (v?: string | Date) =>
  !v ? NaN : v instanceof Date ? v.getTime() : Date.parse(v);

export function useCountdownTo(exp?: string | Date, onExpire?: () => void) {
  const expMs = useMemo(() => toMs(exp), [exp]);
  const ready = Number.isFinite(expMs);  

  const onExpireRef = useRef(onExpire);
  useEffect(() => { onExpireRef.current = onExpire; }, [onExpire]);

  const compute = useCallback(() => {
    if (!ready) return 0;
    return Math.max(0, ((expMs as number) - Date.now()));
  }, [ready, expMs]);

  const [msLeft, setMsLeft] = useState(() => compute());

  useEffect(() => {
    if (!ready) { setMsLeft(0); return; }             // chưa sẵn sàng -> không set timer
    const nowLeft = compute();
    setMsLeft(nowLeft);

    if (nowLeft === 0) {
      const t = setTimeout(() => onExpireRef.current?.(), 0);
      return () => clearTimeout(t);
    }

    const expireTid = window.setTimeout(() => {
      setMsLeft(0);
      onExpireRef.current?.();
    }, nowLeft);

    const firstDelay = nowLeft % 1000 || 1000;
    let iid: number | undefined;
    const firstTid = window.setTimeout(() => {
      setMsLeft(compute());
      iid = window.setInterval(() => setMsLeft(compute()), 1000);
    }, firstDelay);

    return () => {
      clearTimeout(expireTid);
      clearTimeout(firstTid);
      if (iid) clearInterval(iid);
    };
  }, [ready, compute]);

  const secondsLeft = ready ? Math.max(0, Math.ceil(msLeft / 1000)) : undefined;
  const expired = ready ? msLeft <= 0 : undefined;     // ✅ tri-state

  return { ready, expired, msLeft, secondsLeft };
}
